/**
 * H2 (PM kararı, 25 Ekim 2026) — HASSAS YETKİLER ve iki adımlı onay sözleşmesi. TEK KAYNAK: sunucu kuralı
 * (`apps/api/src/lib/capability-approval.ts`, `services/capability-request-service.ts`), veritabanı koruması
 * (`migrations/20261025_0340_capability-approval.sql` — SQL kopyası `tests` ile bu listeye kilitli) ve ekran bu dosyadan türer.
 * Gerekçeli tablo: `docs/security/hassas-yetkiler.md`.
 *
 * Kural: hassas bir yetki bir kullanıcıya YENİ geçerli olacaksa (istisna ile ekleme YA DA rol değişikliğiyle gelme) değişiklik
 * doğrudan uygulanmaz: talep (gerekçe zorunlu) → başka bir uygun kişinin onayı → etkin. Yetki kaldırmak (daraltmak) onay
 * istemez. Çalışma alanı sahibi her zaman tam yetkilidir; sahibin yetkileri değiştirilemez.
 *
 * L1 (PM kararı, 26 Ekim 2026) — SAHİBİN DOĞRUDAN ONAYI: çalışma alanı SAHİBİ, kendi çalışma alanındaki BAŞKA bir kullanıcıya
 * hassas yetkiyi tek adımda verebilir — açık bir sahip işlemi olarak (gizli atlama değil): gerekçe zorunlu, aynı istekte yeniden
 * kimlik doğrulama (parola + iki adımlı doğrulama açıksa kod), denetim kaydı. Kayıt `approvalKind = 'owner_direct'` taşır; iki
 * bağımsız kişinin onayı gibi görünmez. Sahip olmayan yöneticiler için iki kişi kuralı aynen sürer. Çalışma alanı "her zaman iki
 * kişili onay" politikasını (`strictTwoPerson`, varsayılan KAPALI) açtıysa bu yol kapalıdır. Kapsam dışı: sahiplik ya da platform
 * yetkisi devri, kendine verme, sahibin kendi satırı, başka çalışma alanının kullanıcısı. Veritabanı koruması migration
 * 20261026_0370'tedir (aynı koşullar).
 */
import { z } from 'zod';
import { USER_ROLES } from './auth.js';
import { reauthSchema } from './data-lifecycle.js';
import { CAPABILITY_EXCEPTION_REASON, CAPABILITY_NAMES, capabilityExceptionsViewSchema, capabilityOverrideSchema, } from './org.js';
import { ROLE_CAPABILITIES } from './roles.js';
export const SENSITIVE_CATEGORIES = [
    'workspace',
    'people',
    'bulk_data',
    'raw_personal_data',
    'financial',
    'advertising',
];
export const SENSITIVE_CATEGORY_LABELS = {
    workspace: 'Çalışma alanı, bağlantı sırları ve asistan',
    people: 'Kullanıcı ve yetki yönetimi',
    bulk_data: 'Toplu veri işlemleri',
    raw_personal_data: 'Ham kişisel veri',
    financial: 'Mali veri',
    advertising: 'Reklam harcaması',
};
/**
 * Hassas yetkiler ve gerekçeleri. Anahtar sırası ekrandaki sıradır. Liste değişirse: SQL kopyası (yeni migration),
 * `docs/security/hassas-yetkiler.md` ve kilit testleri birlikte güncellenir.
 */
export const SENSITIVE_CAPABILITY_CATALOG = {
    'settings.manage': {
        category: 'workspace',
        reason: 'Çalışma alanı ayarlarının yanında bağlantı sırlarını (kaynak, muhasebe ve web kancası imza sırları, bağlantı kodları), destek erişim kodunu, asistanın yürütme ve bütçe ayarlarını, plan ve modül ayarlarını yönetir.',
    },
    'users.manage': {
        category: 'people',
        reason: 'Kullanıcıların rolünü, durumunu ve yetki istisnalarını değiştirir; yetki talebi açar ve uygun olduğunda onaylar.',
    },
    'users.invite': {
        category: 'people',
        reason: 'Çalışma alanına yeni hesap açar (davet).',
    },
    'departments.manage': {
        category: 'people',
        reason: 'Departman paylaşımını ve teklif onay eşiklerini değiştirir; kimin neyi görüp onaylayacağını belirler.',
    },
    'records.delete': {
        category: 'bulk_data',
        reason: 'Kayıt siler; silinen kayıt geri getirilene kadar kimse göremez.',
    },
    'records.export': {
        category: 'bulk_data',
        reason: 'Kişi ve şirket verisini dosya olarak çalışma alanının dışına çıkarır.',
    },
    'records.import': {
        category: 'bulk_data',
        reason: 'Çok sayıda kaydı tek seferde oluşturur ya da günceller.',
    },
    'records.bulk': {
        category: 'bulk_data',
        reason: 'Seçilen çok sayıda kayıtta tek işlemle değişiklik yapar.',
    },
    'lists.manage': {
        category: 'bulk_data',
        reason: 'Başkalarının kayıtlı listelerini ve paylaşımlarını değiştirir.',
    },
    'lead_events.view_raw': {
        category: 'raw_personal_data',
        reason: 'Aday olayının süzülmemiş ham verisini (form ve reklam yükü) gösterir.',
    },
    'products.cost_view': {
        category: 'financial',
        reason: 'Ürün maliyetini, maliyet tarihçesini ve işlerin iç maliyetini gösterir.',
    },
    'products.price_manage': {
        category: 'financial',
        reason: 'Fiyatları toplu değiştirir ve müşteriye görünmeyen fiyatları yönetir.',
    },
    'ads.approve': {
        category: 'advertising',
        reason: 'Reklam değişikliği isteğini onaylar; onaylanan istek harcamayı etkiler.',
    },
    // K1 (25 Ekim 2026): elle stok hareketi stok değerini ve maliyeti değiştirir. Yetkinin VERİLMESİ iki kişili onaydır; her stok
    // hareketi ayrıca onaya gitmez (hareket defteri kim/ne zaman/önce-sonra miktarını zaten tutar — belge § 1).
    'stock.adjust': {
        category: 'financial',
        reason: 'Elle stok girişi, çıkışı ve sayım düzeltmesi yapar; stok miktarını ve stok değerini değiştirir.',
    },
};
/** Hassas yetkiler (tek kaynak). */
export const SENSITIVE_CAPABILITIES = Object.keys(SENSITIVE_CAPABILITY_CATALOG);
export function isSensitiveCapability(c) {
    return SENSITIVE_CAPABILITIES.includes(c);
}
/**
 * K1 — YÖNETİCİ DAVETİ: davet edilen rol çalışma alanının ayarlarını ya da kullanıcı/yetki yönetimini taşıyorsa (bugün yalnız
 * Sistem yöneticisi) davet, çalışma alanı SAHİBİNİN mevcut yeniden kimlik doğrulamasıyla (parola + açıksa iki adımlı doğrulama
 * kodu; `reauthSchema`) yapılır. Bu, çalışma alanı içinde yöneticilik verir; Castinflow platform işletmeciliği (operatör konsolu)
 * vermez — ikisi ayrı kimlik ve ayrı rol tablosudur.
 */
export const ADMIN_INVITE_CAPABILITIES = [
    'settings.manage',
    'users.manage',
    'users.invite',
    'departments.manage',
];
export function inviteNeedsReauth(role) {
    return ROLE_CAPABILITIES[role].some((c) => ADMIN_INVITE_CAPABILITIES.includes(c));
}
/** Onaylanmamış talep bu süre sonunda düşer (süre esaslı: talep anı + 7 gün, UTC anı aritmetiği). */
export const CAPABILITY_REQUEST_PENDING_DAYS = 7;
/** Süreli istisna sınırları: en kısa 15 dakika, en uzun 365 gün (süre esaslı). */
export const TIMED_GRANT_LIMITS = { minMinutes: 15, maxDays: 365 };
/** Rol + istisna → geçerli yetkiler (sahip hariç; sunucunun `capabilitiesFor` kuralıyla aynı) */
export function capabilitiesOf(role, override) {
    const s = new Set(ROLE_CAPABILITIES[role]);
    for (const g of override.grant)
        s.add(g);
    for (const r of override.revoke)
        s.delete(r);
    return CAPABILITY_NAMES.filter((c) => s.has(c));
}
/** Değişikliğin YENİ geçerli kıldığı hassas yetkiler (boşsa onay gerekmez) */
export function sensitiveAdditions(before, after) {
    const had = new Set(capabilitiesOf(before.role, before.override));
    return capabilitiesOf(after.role, after.override).filter((c) => !had.has(c) && isSensitiveCapability(c));
}
export const CAPABILITY_REQUEST_STATUSES = [
    'pending',
    'approved',
    'rejected',
    'cancelled',
    'expired',
];
export const CAPABILITY_REQUEST_STATUS_LABELS = {
    pending: 'Onay bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
    cancelled: 'Geri çekildi',
    expired: 'Süresi doldu',
};
/** Onay veremeyiş nedenleri (sunucu ile ekran aynı kodu kullanır) */
export const CAPABILITY_DECIDE_BLOCKS = [
    'not_pending',
    'expired',
    'self_requested',
    'target_self',
    'inactive',
    'no_users_manage',
    'outranked',
    'lacks_capability',
];
/** Onay kaydının türü (L1): iki bağımsız kişi ya da sahibin doğrudan onayı. Kayıt ve ekran ikisini ayırır. */
export const CAPABILITY_APPROVAL_KINDS = ['two_person', 'owner_direct'];
export const CAPABILITY_APPROVAL_KIND_LABELS = {
    two_person: 'İki kişili onay',
    owner_direct: 'Sahibin doğrudan onayı',
};
/** Sahibin doğrudan verme yolu neden kapalı (sunucu ile ekran aynı kodu kullanır) */
export const OWNER_DIRECT_BLOCKS = ['not_owner', 'strict_policy', 'target_owner', 'self'];
const reasonText = z
    .string()
    .trim()
    .min(CAPABILITY_EXCEPTION_REASON.min, 'gerekçe en az 10 karakter')
    .max(CAPABILITY_EXCEPTION_REASON.max, 'gerekçe en çok 500 karakter');
const capabilityChangeShape = {
    /** Yeni rol (rol değişikliği talebi); verilmezse rol değişmez */
    role: z.enum(USER_ROLES).optional(),
    /** Yeni istisna (tamamı); verilmezse saklanan istisna korunur */
    capabilities: capabilityOverrideSchema.optional(),
    reason: reasonText,
    /** Süreli istisna: istisna ile eklenen yetkiler bu anda etkisizleşir (ISO 8601, ofsetli) */
    grantExpiresAt: z.iso.datetime({ offset: true }).optional(),
    /** İyimser kilit: ekranın gördüğü rol + istisna; değiştiyse 409 */
    expected: z.object({ role: z.enum(USER_ROLES), override: capabilityOverrideSchema }),
};
const roleOrCapabilities = (v) => v.role !== undefined || v.capabilities !== undefined;
/** `POST /users/:id/capability-requests` gövdesi */
export const createCapabilityRequestInputSchema = z
    .object(capabilityChangeShape)
    .strict()
    .refine(roleOrCapabilities, { message: 'rol ya da istisna gerekli' });
/**
 * L1 — `POST /users/:id/capability-grants/owner-direct` gövdesi: talep gövdesi + sahibin AYNI istekteki yeniden kimlik doğrulaması
 * (parola + açıksa iki adımlı doğrulama kodu). Yeniden kimlik doğrulama eksikse sunucu 403 `reauth_required` döner (alan
 * şemada isteğe bağlıdır ki neden kodu ekrana açık gelsin).
 */
export const ownerDirectGrantInputSchema = z
    .object({ ...capabilityChangeShape, reauth: reauthSchema.optional() })
    .strict()
    .refine(roleOrCapabilities, { message: 'rol ya da istisna gerekli' });
/**
 * L1 — çalışma alanının hassas yetki politikası (`PUT /capability-policy`; yalnız sahip). `strictTwoPerson = true`: sahibin
 * doğrudan verme yolu kapanır, her hassas yetki iki bağımsız kişinin onayıyla verilir. Kapatmak (gevşetmek) yeniden kimlik
 * doğrulama ister.
 */
export const capabilityPolicyInputSchema = z
    .object({ strictTwoPerson: z.boolean(), reauth: reauthSchema.optional() })
    .strict();
export const capabilityPolicySchema = z.object({
    strictTwoPerson: z.boolean(),
    /** Çağıran sahip ve politika izin veriyor: sahibin doğrudan verme yolu açık */
    ownerDirectAvailable: z.boolean(),
});
export const decideCapabilityRequestInputSchema = z
    .object({ note: z.string().trim().max(500).optional() })
    .strict();
export const rejectCapabilityRequestInputSchema = z
    .object({ note: z.string().trim().min(3, 'ret gerekçesi en az 3 karakter').max(500) })
    .strict();
const person = z.object({ id: z.string(), name: z.string() });
const caps = z.array(z.enum(CAPABILITY_NAMES));
export const capabilityRequestSchema = z.object({
    id: z.guid(),
    /** L1: onay türü — sahibin doğrudan onayı iki bağımsız kişinin onayı gibi gösterilmez (eski yanıt: iki kişili) */
    approvalKind: z.enum(CAPABILITY_APPROVAL_KINDS).default('two_person'),
    /** Saklanan durum; süresi geçmiş bekleyen talep `expired` gösterilir (okuma anı denetimi) */
    status: z.enum(CAPABILITY_REQUEST_STATUSES),
    target: person.extend({ role: z.enum(USER_ROLES) }),
    requestedBy: person.nullable(),
    requestedAt: z.string(),
    /** Onaylanmazsa düşeceği an */
    expiresAt: z.string(),
    before: z.object({ role: z.enum(USER_ROLES), override: capabilityOverrideSchema }),
    after: z.object({ role: z.enum(USER_ROLES), override: capabilityOverrideSchema }),
    added: caps,
    removed: caps,
    sensitiveAdded: caps,
    grantExpiresAt: z.string().nullable(),
    reason: z.string(),
    decidedBy: person.nullable(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    /** Çağıran onaylayabilir/reddedebilir mi; veremiyorsa neden */
    canDecide: z.boolean(),
    decideBlockedReason: z.enum(CAPABILITY_DECIDE_BLOCKS).nullable(),
    canCancel: z.boolean(),
    /**
     * Bekleyen talepte uygun onaylayıcılar (talep eden ve hedef hariç; yalnız ad). `count = 0` → kimse onaylayamaz: talep bekler ve
     * süresi dolunca düşer; ekran güvenli yolu anlatır.
     */
    eligibleApprovers: z
        .object({
        count: z.number().int().min(0),
        people: z.array(person.extend({ isOwner: z.boolean() })),
    })
        .nullable(),
});
/** `GET /capability-requests` — onay merkezi "Yetki talepleri" (her listenin sayısı uzunluğudur) */
export const capabilityRequestsOverviewSchema = z.object({
    /** Çağıranın karar verebileceği bekleyen talepler */
    inbox: z.array(capabilityRequestSchema),
    /** Çağıranın açtığı bekleyen talepler */
    mine: z.array(capabilityRequestSchema),
    /** Son karar verilen / düşen talepler (yalnız `users.manage`; en çok 50) */
    history: z.array(capabilityRequestSchema),
    canManage: z.boolean(),
    /** L1: çalışma alanının politikası (eski yanıtta yok) */
    policy: capabilityPolicySchema.optional(),
});
/** G3 görünümü + H2: bekleyen talep ve süreli istisnaların bitiş anları */
export const capabilityExceptionsViewH2Schema = capabilityExceptionsViewSchema.extend({
    pendingRequest: capabilityRequestSchema.nullable().optional(),
    grantTerms: z
        .array(z.object({ capability: z.enum(CAPABILITY_NAMES), expiresAt: z.string() }))
        .optional(),
    /** L1: sahibin doğrudan verme yolu bu kullanıcı için açık mı; kapalıysa neden (eski yanıtta yok = kapalı) */
    ownerDirect: z
        .object({ available: z.boolean(), blockedReason: z.enum(OWNER_DIRECT_BLOCKS).nullable() })
        .optional(),
});
//# sourceMappingURL=capability-approval.js.map